import { useTranslation } from "react-i18next";
import type { TFunction } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { Breadcrumb } from "../../../../../components/ProductsLayout/SharedProductsLayout";
import { LinkUnstyled } from "../../../../../components/LinkUnstyled/LinkUnstyled";
import {
  ButtonWithConfirmDialog,
  DestructiveButtonMediumV2,
  InvisibleButton,
  LinkButtonMediumV2,
  PrimaryButtonMediumV2,
  PrimaryButtonWithPlusIcon,
  TertiaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import {
  BorderCard,
  PageWrapper,
  TwoColumGrid,
} from "../../../../../layout/portalPageLayout";
import { ContentWrapper } from "../../../../../layout/publicPageLayout";
import { useRoutePath } from "../../../../../util/Routing";
import { useStoreState } from "../../../../../util/util";
import { TemplatesNav, TemplatesNavWithPadding } from "./TemplatesNav";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";
import type {
  AttributeTemplateSchema,
  TemplateTabItem,
  TemplateViewConfigurstions,
  TemplateViewSchema,
} from "../../../../../types/types.PIM";
import type { TemplateLocationState } from "../../../../../types/types";
import useSWR from "swr";
import type { AxiosError } from "axios";
import type { MouseEvent } from "react";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TemplateViewCard } from "./TemplateViewCard";
import { ButtonGroup } from "../../../../../components/EditableTitle/EditableTitle";
import { useForm } from "react-hook-form";
import { EditModeTitle } from "../../../../../components/EditableTitle/EditModeTitle";
import {
  CaretDownIcon,
  CaretUpIcon,
  CheckIcon,
  DeleteIcon,
  EditIcon,
  PlusIcon,
  ViewIcon,
  WarningIcon,
  XIcon,
} from "../../../../../components/Icons/Icons";
import { screenSize } from "../../../../../theme";
import { Flex } from "../../../../../layout/FormLayout";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { Modal } from "../../../../../components/Modal/Modal";
import styled, { useTheme } from "styled-components";
import { zodRequiredString } from "../../../../../util/zod.util";
import { useAuthContext } from "../../../../../components/Auth";
import { InfoBlockExtraSmall } from "../../../../../components/InfoBlocks/InfoBlocks";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { TemplateViewConfigurationForm } from "./TemplateViewConfigurationForm";
import type { ExpandedState } from "@tanstack/react-table";
import { fontSizeTokens, spacingTokens } from "../../../../../designTokens";
import { AccordionCard } from "../../../../../components/AccordionCard/AccordionCard";
import { TextXlBold } from "../../../../../components/Typography/TypographyV2";
import { DropDownModal } from "../../../../../components/DropDownModal/DropDownModal";

const SaveButtonContainer = styled.div`
  display: flex;
  align-content: center;
  align-items: center;
  margin-left: auto;
`;

const StickyHeaderSection = styled.div`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.primaryBG};
  z-index: 10;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const DeleteViewButtonContainer = styled.div`
  button {
    padding: 10px 15px;
  }
  margin-top: 5px;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const ModalWrapper = styled.div`
  padding: 50px 30px ${({ theme }) => theme.resolve("spacing_2xl")};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.small};
`;

const WarningTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  margin-bottom: 30px;
`;

export const TabItemNameSchema = z.object({
  name: z.string(),
});

export const ChipBox = styled.div`
  display: inline-block;
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  border: solid ${({ theme }) => theme.resolve("border_md")}
    ${({ theme }) => theme.secondaryBorder};
  background-color: ${({ theme }) => theme.tertiaryButtonBG};
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  margin: 3px 5px 5px 0;
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.primaryTextColor};
`;

const ViewsDropDownWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.resolve("radius_button_standard")};
  background: ${({ theme }) => theme.tertiaryButtonSelected};
  padding: ${({ theme }) =>
    `${theme.resolve("spacing_sm")} ${theme.resolve("spacing_lg")}`};
  border: solid ${({ theme }) => theme.resolve("border_md")}
    ${({ theme }) => theme.selectedBorder};
  @media ${screenSize.small} {
    margin-right: 5px;
  }
`;

const ViewsDropDown = styled.div`
  position: absolute;
  top: 100%;
  left: -1px;
  min-width: 100%;
  z-index: 1;
  white-space: nowrap;
`;

const UpdatedBorderCard = styled(BorderCard)`
  .accordion-content-wrapper {
    padding: 0;
  }
  div.template-view-card-wrapper:not(:has(~ div.template-view-card-wrapper)) {
    margin-bottom: 0 !important;
  }
`;

export type TabItemFormValue = z.infer<typeof TabItemNameSchema>;
export const TabItemNameSchemaFn = (t: TFunction, items: string[]) =>
  z.object({
    name: zodRequiredString(t).refine(
      (val) => Boolean(items.indexOf(val.toLocaleLowerCase()) < 0),
      {
        message: t("Duplicate tab name not allowed"),
      }
    ),
  });

export function SellerAdminTemplateViewTab() {
  const [addNewTab, setAddNewTab] = useState(false);
  const [showSaveButton, setshowSaveButton] = useState(false);
  const [currentView, setCurrentView] =
    useState<{ id: string; name: string }>();
  const [showConfigurationForm, setShowConfigurationForm] = useState(false);
  const [configurationsFormData, setConfigurationFormData] = useState<{
    viewName?: string;
    viewId?: string;
    templateId?: string;
    config?: TemplateViewConfigurstions;
  }>({});
  const [showViewsDropDown, setShowViewsDropDown] = useState(false);
  const [expandedRows, setExpandedRows] = useState<{
    [key: string]: ExpandedState;
  }>({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { t } = useTranslation();
  const { tenant_id, storefront_id, storefront_metadata } = useStoreState();
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();
  const { adminPath } = useRoutePath();
  const { template_id } = useParams<{ template_id: string }>();
  const location = useLocation();
  const theme = useTheme();

  const ViewswrapperRef = useRef<HTMLDivElement>(null);

  const { hasPermission } = useAuthContext();
  const { notifyError, notifySuccess } = useContext(Notifications);

  const { data: template, mutate: mutateTemplate } = useSWR<
    AttributeTemplateSchema,
    AxiosError
  >(`/v2/tenants/${tenant_id}/pim/templates/${template_id}`, {
    revalidateOnFocus: showSaveButton ? false : true,
  });

  const templateDisplayName = getTemplateDisplayName(
    template ?? {},
    t("Template")
  );

  const activeViewId =
    currentView?.id ??
    template?.views.find((view) => view.name?.toLocaleLowerCase() === "default")
      ?.id;

  const { data: viewData, mutate: mutateViewData } = useSWR<
    TemplateViewSchema,
    AxiosError
  >(
    activeViewId
      ? `/v2/storefronts/${storefront_id}/pim/templates/${template_id}/views/${activeViewId}`
      : null,
    {
      revalidateOnFocus: showSaveButton ? false : true,
    }
  );

  const editPermissionsEnabled =
    storefront_metadata.enable_attribute_level_role_based_edit_permissions;

  const viewIsExternalOnly = useMemo(
    () =>
      (viewData?.configurations?.assigned_roles?.length ?? 0) === 0 &&
      (viewData?.configurations?.is_assigned_to_customer ||
        viewData?.configurations?.is_assigned_to_distributor ||
        viewData?.configurations?.is_assigned_to_outside_login),
    [
      viewData?.configurations?.assigned_roles?.length,
      viewData?.configurations?.is_assigned_to_customer,
      viewData?.configurations?.is_assigned_to_distributor,
      viewData?.configurations?.is_assigned_to_outside_login,
    ]
  );

  const viewHasMixedRoles = useMemo(
    () =>
      (viewData?.configurations?.assigned_roles?.length ?? 0) > 0 &&
      (viewData?.configurations?.is_assigned_to_customer ||
        viewData?.configurations?.is_assigned_to_distributor ||
        viewData?.configurations?.is_assigned_to_outside_login),
    [
      viewData?.configurations?.assigned_roles?.length,
      viewData?.configurations?.is_assigned_to_customer,
      viewData?.configurations?.is_assigned_to_distributor,
      viewData?.configurations?.is_assigned_to_outside_login,
    ]
  );

  const [cards, setCards] = useState(viewData?.layout || []);
  const inputRef = useRef<HTMLInputElement>(null);
  const editNameUseForm = useForm({
    resolver: zodResolver(
      TabItemNameSchemaFn(
        t,
        viewData?.layout?.map((item) =>
          item.display_name.toLocaleLowerCase()
        ) || []
      )
    ),
  });

  const update_priority = <ItemType extends { priority: number }>(
    items: ItemType[]
  ) => {
    return items.map((item, idx) => ({ ...item, priority: idx }));
  };

  const updateRow = ({
    source,
    data,
  }: {
    source: string;
    data: TemplateTabItem[];
  }) => {
    setshowSaveButton(true);
    const newCards = [...cards];
    const cardIndex = cards.findIndex((card) => card.name === source);
    newCards[cardIndex].items = data;
    setCards(newCards);
  };

  const moveRow = ({
    item,
    source,
    data,
  }: {
    item: TemplateTabItem;
    source: string;
    data: TemplateTabItem[];
  }) => {
    setCards((prevCards) => {
      const oldSourceIndex = prevCards.findIndex((card) =>
        card?.items.includes(item)
      );
      const newSourceIndex = prevCards.findIndex(
        (card) => card?.name === source
      );
      const newCards = [...prevCards];
      if (oldSourceIndex === newSourceIndex) {
        newCards[newSourceIndex] = {
          ...newCards[newSourceIndex],
          items: data,
        };
        return update_priority(newCards);
      }
      setshowSaveButton(true);
      if (oldSourceIndex > -1) {
        newCards[oldSourceIndex] = {
          ...newCards[oldSourceIndex],
          items: newCards[oldSourceIndex].items.filter(
            (existingItem) => existingItem !== item
          ),
        };
      }
      if (newSourceIndex > -1) {
        newCards[newSourceIndex] = {
          ...newCards[newSourceIndex],
          items: data,
        };
      }
      return update_priority(newCards);
    });
  };

  const handleAddNewTab = async ({ name }: TabItemFormValue) => {
    try {
      await axios.post(
        `v2/tenants/${tenant_id}/templates/${template_id}/views/${currentView?.id}/tabs`,
        {
          name,
        }
      );
      await mutateViewData();
      setAddNewTab(false);
      notifySuccess(t(`Tab (${name}) has been created successfully`));
    } catch (error) {
      await mutateViewData();
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage ? errorMessage : t("There was an error editing tab name"),
        {
          error,
        }
      );
    }
  };

  const handleToggleAllRowsExpanded = (expanded: boolean) => {
    if (!expanded) {
      setExpandedRows({});
    }
  };

  const submitTemplateViewData = useCallback(async () => {
    try {
      await axios.put(
        `v2/storefronts/${storefront_id}/pim/templates/${template_id}/views/${currentView?.id}`,
        { layout: cards }
      );
      setShowConfirmationModal(false);
      setshowSaveButton(false);
      notifySuccess(t("Template view has been updated successfully"));
      await mutateViewData();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error editing template view"),
        {
          error,
        }
      );
    }
  }, [
    cards,
    mutateViewData,
    notifyError,
    notifySuccess,
    t,
    template_id,
    currentView,
    storefront_id,
  ]);

  const saveReorderChanges = () => {
    if (template?.number_of_products && template?.number_of_products > 0) {
      setShowConfirmationModal(true);
    } else {
      submitTemplateViewData();
    }
  };

  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (dragIndex !== hoverIndex) {
        const list = [...cards];
        list.splice(hoverIndex, 0, list.splice(dragIndex, 1)[0]);
        setCards(update_priority(list));
        setshowSaveButton(true);
      }
    },
    [cards]
  );
  const handleEditConfiguration = () => {
    setConfigurationFormData({
      viewId: viewData?.id,
      templateId: template_id,
      viewName: viewData?.name,
      config: viewData?.configurations,
    });
    setShowConfigurationForm(true);
  };

  const handleSelectView = (event: MouseEvent<HTMLButtonElement>) => {
    const view = template?.views.find(
      ({ name }) => name === event.currentTarget.dataset.name
    );
    if (view) {
      setCurrentView(view);
    }
  };

  const handleClickOutsideViews = (event: any) => {
    if (
      ViewswrapperRef.current &&
      !ViewswrapperRef.current.contains(event.target)
    ) {
      setShowViewsDropDown(false);
    }
  };

  const handleCardExpandedState = (
    cardId: string,
    expandedState: ExpandedState
  ) => {
    setExpandedRows((prev) => {
      let newExpanded = prev;
      newExpanded[cardId] = expandedState;
      return newExpanded;
    });
  };

  const handleSubmittedView = (view: TemplateViewSchema) => {
    mutateViewData();
    setShowConfigurationForm(false);
    mutateTemplate();
    setCurrentView({ id: view.id, name: view.name });
  };

  const handleDeleteView = async () => {
    try {
      await axios.delete(
        `/v2/storefronts/${storefront_id}/pim/templates/${template_id}/views/${currentView?.id}`
      );
      mutateTemplate();
      notifySuccess(t("View has been deleted successfully."));
      setCurrentView(
        template?.views.find(
          (view) => view.name?.toLocaleLowerCase() === "default"
        )
      );
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error deleting the template view"),
        {
          error,
        }
      );
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutsideViews, false);
    return () => {
      document.removeEventListener("click", handleClickOutsideViews, false);
    };
  }, []);

  useEffect(() => {
    if (template && !currentView) {
      setCurrentView(
        template.views.find(
          (view) => view.name?.toLocaleLowerCase() === "default"
        )
      );
    }
  }, [template, currentView]);

  useEffect(() => {
    if (viewData?.layout) setCards(viewData.layout);
  }, [viewData]);

  return (
    <PageWrapper>
      <Breadcrumb
        style={{ padding: `${spacingTokens.spacing_xl}`, paddingBottom: 0 }}
      >
        <LinkUnstyled
          to={{
            pathname: location.search.includes("from=dashboard")
              ? `${adminPath}/dashboard`
              : `${adminPath}/pim/templates`,
            state: {
              ...(location.state as TemplateLocationState),
              templateType:
                template?.template_type ??
                (location.state as TemplateLocationState)?.templateType,
            },
          }}
        >
          {location.search.includes("from=dashboard")
            ? t("Dashboard")
            : t("Templates")}
        </LinkUnstyled>
        <div>/</div>
        <div>{templateDisplayName}</div>
      </Breadcrumb>
      {template && (
        <TemplatesNavWithPadding>
          <TemplatesNav
            pageTitle={templateDisplayName}
            tabIndex={4}
            templateID={template_id}
            enableCustomForms={template?.enable_custom_forms}
          />
        </TemplatesNavWithPadding>
      )}
      {template && viewData && (
        <>
          <ContentWrapper>
            <StickyHeaderSection>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "start",
                  padding: `${spacingTokens.spacing_xl}`,
                  gap: spacingTokens.spacing_lg,
                }}
              >
                <TextXlBold>{t("View")}:</TextXlBold>
                <ViewsDropDownWrapper
                  ref={ViewswrapperRef}
                  onClick={() => setShowViewsDropDown(!showViewsDropDown)}
                >
                  <ViewIcon width={20} height={20} />
                  <span
                    style={{
                      padding: "0 10px 0 5px",
                      fontSize: fontSizeTokens.font_size_md,
                    }}
                    onClick={() => setShowViewsDropDown(!showViewsDropDown)}
                  >
                    {currentView?.name}
                  </span>
                  {showViewsDropDown ? (
                    <CaretUpIcon width={20} height={20} />
                  ) : (
                    <CaretDownIcon width={20} height={20} />
                  )}
                  {showViewsDropDown && template.views.length > 0 && (
                    <ViewsDropDown>
                      <DropDownModal
                        items={template.views.map((view) => view.name)}
                        activeItem=""
                        clickHandler={handleSelectView}
                      />
                    </ViewsDropDown>
                  )}
                </ViewsDropDownWrapper>
                {hasPermission("modify_templates") && !showSaveButton && (
                  <TertiaryButtonMediumV2
                    onClick={() => {
                      setConfigurationFormData({ templateId: template_id });
                      setShowConfigurationForm(true);
                    }}
                  >
                    <PlusIcon width={16} height={16} />
                    {t("New")}
                  </TertiaryButtonMediumV2>
                )}
                {showSaveButton && viewData.layout && (
                  <SaveButtonContainer>
                    <TertiaryButtonMediumV2
                      onClick={() => {
                        setCards([...viewData.layout]);
                        setshowSaveButton(false);
                        mutateViewData();
                      }}
                      style={{ marginRight: "15px" }}
                    >
                      {t("Cancel")}
                    </TertiaryButtonMediumV2>
                    <PrimaryButtonMediumV2 onClick={() => saveReorderChanges()}>
                      {t("Save")}
                    </PrimaryButtonMediumV2>
                  </SaveButtonContainer>
                )}
              </div>
            </StickyHeaderSection>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                padding: `0 ${spacingTokens.spacing_xl}`,
              }}
            >
              <BorderCard>
                <AccordionCard
                  title={t("Configuration")}
                  expanded={true}
                  hideHeaderBorder={true}
                  actionChildren={() => (
                    <>
                      {!showConfigurationForm &&
                        hasPermission("modify_templates") && (
                          <LinkButtonMediumV2
                            onClick={() => handleEditConfiguration()}
                          >
                            <EditIcon
                              height={16}
                              width={16}
                              fill={theme.secondaryTextColor}
                            />
                            {t("Edit")}
                          </LinkButtonMediumV2>
                        )}
                    </>
                  )}
                >
                  <div
                    style={{
                      marginTop: `10px`,
                    }}
                  >
                    <InfoBlockExtraSmall
                      header={t("View Name")}
                      content={currentView?.name}
                    />
                  </div>
                  <TwoColumGrid>
                    <div
                      style={{
                        marginTop: `10px`,
                      }}
                    >
                      <InfoBlockExtraSmall
                        header={t("Internal Assignment")}
                        content={
                          <>
                            {viewData.configurations?.assigned_roles &&
                            viewData.configurations.assigned_roles.length >
                              0 ? (
                              viewData.configurations.assigned_roles.map(
                                (role) => (
                                  <ChipBox key={role.id}>{role.name}</ChipBox>
                                )
                              )
                            ) : (
                              <span>--</span>
                            )}
                          </>
                        }
                      />
                    </div>
                    {template?.template_type === "product" && (
                      <>
                        <div
                          style={{
                            marginTop: `10px`,
                          }}
                        >
                          <InfoBlockExtraSmall
                            header={t("Customer Assignment")}
                            content={
                              viewData.configurations?.is_assigned_to_customer
                                ? t("Yes")
                                : t("No")
                            }
                          />
                        </div>
                        <div
                          style={{
                            marginTop: `10px`,
                          }}
                        >
                          <InfoBlockExtraSmall
                            header={t("Distributor Assignment")}
                            content={
                              viewData.configurations
                                ?.is_assigned_to_distributor
                                ? t("Yes")
                                : t("No")
                            }
                          />
                        </div>
                        <div
                          style={{
                            marginTop: `10px`,
                          }}
                        >
                          <InfoBlockExtraSmall
                            header={t("Public Assignment")}
                            content={
                              viewData.configurations
                                ?.is_assigned_to_outside_login
                                ? t("Yes")
                                : t("No")
                            }
                          />
                        </div>
                      </>
                    )}
                  </TwoColumGrid>
                </AccordionCard>
              </BorderCard>
              <UpdatedBorderCard>
                <AccordionCard
                  title={t("Customization")}
                  subtitle={
                    editPermissionsEnabled && !viewIsExternalOnly
                      ? t(
                          "Rearrange groups and collections and configure which attributes you want to be visible or editable for this view"
                        )
                      : t(
                          "Rearrange groups and collections and configure which attributes you want to be visible for this view"
                        )
                  }
                  expanded={true}
                  hideHeaderBorder={true}
                  actionChildren={() => <></>}
                >
                  {currentView &&
                    cards &&
                    cards.map((card, i) => (
                      <TemplateViewCard
                        key={card.id}
                        expandedRows={expandedRows[card.id]}
                        handleExpandedState={handleCardExpandedState}
                        disabled={showSaveButton}
                        editPermissionsEnabled={editPermissionsEnabled}
                        viewIsExternalOnly={!!viewIsExternalOnly}
                        viewHasMixedRoles={!!viewHasMixedRoles}
                        templateId={template_id}
                        viewId={currentView?.id}
                        index={i}
                        card={card}
                        moveCard={moveCard}
                        moveRow={moveRow}
                        updateRow={updateRow}
                        mutateView={mutateViewData}
                        templateType={
                          template?.template_type !== null
                            ? template?.template_type
                            : undefined
                        }
                        handleToggleAllRowsExpanded={
                          handleToggleAllRowsExpanded
                        }
                        handleDrop={
                          showSaveButton
                            ? undefined
                            : () => setshowSaveButton(true)
                        }
                        tabNames={viewData?.layout.map((item) =>
                          item.display_name.toLocaleLowerCase()
                        )}
                      />
                    ))}
                  {addNewTab && (
                    <Flex
                      style={{
                        margin: `${spacingTokens.spacing_2xl} 0`,
                      }}
                    >
                      <form
                        id="add_new"
                        noValidate
                        onSubmit={editNameUseForm.handleSubmit(handleAddNewTab)}
                        style={{ position: "relative" }}
                      >
                        <EditModeTitle
                          name={"name"}
                          theref={inputRef}
                          register={editNameUseForm.register}
                          formState={editNameUseForm.formState}
                          errors={editNameUseForm.errors}
                          tabIndex={0}
                          defaultValue={""}
                          fontSize={"regular"}
                          fontWeight={"large"}
                        />
                      </form>
                      <ButtonGroup>
                        <InvisibleButton form={"add_new"} type="submit">
                          <CheckIcon fill={theme.activeToggleBG} />
                        </InvisibleButton>
                        <InvisibleButton
                          type="button"
                          onClick={() => setAddNewTab(false)}
                        >
                          <XIcon fill={theme.destructiveTextColor} />
                        </InvisibleButton>
                      </ButtonGroup>
                    </Flex>
                  )}
                  {!addNewTab &&
                    viewData?.layout &&
                    viewData?.layout.length > 0 &&
                    hasPermission("modify_templates") && (
                      <div style={{ marginTop: spacingTokens.spacing_2xl }}>
                        <PrimaryButtonWithPlusIcon
                          useV2
                          onClick={() => setAddNewTab(true)}
                        >
                          {t("Add Tab")}
                        </PrimaryButtonWithPlusIcon>
                      </div>
                    )}
                </AccordionCard>
              </UpdatedBorderCard>
              {hasPermission("delete_templates") && (
                <DeleteViewButtonContainer>
                  <ButtonWithConfirmDialog
                    Button={(props) => (
                      <DestructiveButtonMediumV2 {...props}>
                        {
                          <>
                            <DeleteIcon /> {t("Delete View")}
                          </>
                        }
                      </DestructiveButtonMediumV2>
                    )}
                    buttonText={t("Delete View")}
                    testid={"delete-template-view"}
                    tooltipPlace="right"
                    handleConfirm={() => handleDeleteView()}
                    confirmMessage={
                      "Are you sure you want to delete this view?"
                    }
                    disabled={
                      currentView?.name.toLocaleLowerCase() === "default" ||
                      (viewData.configurations?.assigned_roles?.length ?? 0) >
                        0 ||
                      !hasPermission("delete_templates")
                    }
                    datatip={
                      currentView?.name.toLocaleLowerCase() === "default" &&
                      template.views.length === 1
                        ? t("The 'Default' view cannot be deleted.")
                        : (viewData.configurations?.assigned_roles?.length ??
                            0) > 0
                        ? "View cannot be deleted, Remove roles from view first"
                        : ""
                    }
                    datafor="delete-template-view"
                  />
                </DeleteViewButtonContainer>
              )}
            </div>
          </ContentWrapper>
          <Modal
            overlay
            show={showConfirmationModal}
            closeModal={() => {
              setShowConfirmationModal(false);
              setCards([...viewData?.layout]);
            }}
            modalWidth={"600px"}
          >
            <ModalWrapper>
              <div style={{ marginBottom: "15px" }}>
                <WarningIcon width={50} height={50} />
              </div>
              <WarningTitle>
                {t(
                  `{{numberOfProducts}} {{product}} will get affected by this change, Are you sure you want to confirm changes?`,
                  {
                    numberOfProducts: template?.number_of_products,
                    product:
                      template?.number_of_products === 1
                        ? "product"
                        : "products",
                  }
                )}
              </WarningTitle>
              <div style={{ display: "flex", marginTop: "30px" }}>
                <TertiaryButtonMediumV2
                  fullWidth
                  onClick={() => {
                    setShowConfirmationModal(false);
                    setCards([...viewData?.layout]);
                  }}
                  style={{ marginRight: "10px" }}
                >
                  {t("Cancel")}
                </TertiaryButtonMediumV2>
                <PrimaryButtonMediumV2
                  fullWidth
                  onClick={() => submitTemplateViewData()}
                  style={{ marginLeft: "10px" }}
                >
                  {t("Confirm")}
                </PrimaryButtonMediumV2>
              </div>
            </ModalWrapper>
          </Modal>
          <SlideOut
            title={t("View Configuration")}
            closeFlyout={() => setShowConfigurationForm(false)}
            show={showConfigurationForm}
          >
            <TemplateViewConfigurationForm
              handleCancel={() => setShowConfigurationForm(false)}
              configurations={configurationsFormData}
              handleSubmittedView={handleSubmittedView}
              showConfirmationModal={showConfirmationModal}
              numberOfProducts={template?.number_of_products}
              numberOfViews={template.views.length}
              templateType={template?.template_type}
            />
          </SlideOut>
        </>
      )}
    </PageWrapper>
  );
}
